import { useReducer, useMemo, useCallback, useEffect, useState } from 'react';
import type { KnightKey, BuildData, NimueUpgradeOption, NimueSelection } from '../types';
import { useStorage } from './useStorage';
import { SWORN_DATA } from '../gameData';
import { usePanelState } from './modules/usePanelState';
import { useContextMenu } from './useContextMenu';
import { constructBuildData } from '../utils/buildHelpers';
import { buildReducer, BuildState } from '../reducers/buildReducer';

const defaultSourceOptions = Object.keys(SWORN_DATA.source_modifiers).reduce((acc, key) => {
    acc[key] = 'A';
    return acc;
}, {} as Record<string, 'A' | 'B'>);

const INITIAL_STATE: BuildState = {
    selectedKnight: 'vigilante',
    selectedWeapon: 'sword',
    selectedSpell: 'dagger_flurry',
    selectedBlessings: new Set(),
    
    nimueSelection: { option1: null, option2: null },
    anvil: { mode: 'single', upgrades: [] },
    morgana: { mode: 'single', modifiers: [] },
    merlin: { mode: 'single', upgrades: [] },
    source: defaultSourceOptions,

    currentBuildId: null,
    currentBuildName: null,
    viewedBlessing: null
};

export const useSwornBuilder = () => {
    const { saveBuild, getAllBuilds, deleteBuild, updateBuild, shareBuild, getSharedBuild, isReady } = useStorage();
    const [state, dispatch] = useReducer(buildReducer, INITIAL_STATE);
    const [savedBuilds, setSavedBuilds] = useState<BuildData[]>([]);
    
    // We treat saveMessage separate from global reducer state as it is transient UI state
    const [saveMessage, setSaveMessage] = useState<string | null>(null);

    const {
        activePanel, setActivePanel,
        selectedCompendiumKnight, setSelectedCompendiumKnight,
        selectedCompendiumFae, setSelectedCompendiumFae,
        closeAllPanels,
        activatePanel
    } = usePanelState();

    const {
        containerRef,
        contextMenu,
        handleContextMenu: openContextMenu,
        closeContextMenu
    } = useContextMenu();

    useEffect(() => {
        if (isReady) {
            getAllBuilds()
                .then(setSavedBuilds)
                .catch(err => console.error('Failed to load builds:', err));
        }
    }, [isReady, getAllBuilds]);

    useEffect(() => {
        if (isReady) {
            const params = new URLSearchParams(window.location.search);
            const sharedId = params.get('build');
            if (sharedId) {
                getSharedBuild(sharedId).then(build => {
                    if (build) {
                        dispatch({ type: 'LOAD_BUILD', build });
                        setSaveMessage("Shared build loaded!");
                        window.history.replaceState({}, '', window.location.pathname);
                    } else {
                        setSaveMessage("Shared build not found.");
                    }
                });
            }
        }
    }, [isReady, getSharedBuild]);

    const knightKeys = useMemo(() => Object.keys(SWORN_DATA.classes) as KnightKey[], []);

    const currentNimueModifiers = useMemo(() => {
        if (!state.selectedKnight || !state.selectedWeapon) return null;
        const weaponData = SWORN_DATA.classes[state.selectedKnight]?.weapons[state.selectedWeapon];
        const upgrades = weaponData?.nimue_upgrades || [];
        const option1Data = upgrades[0];
        const option2Data = upgrades[1];

        const getModText = (mod: NimueUpgradeOption | undefined, label: string) => mod ? `[${label}] ${mod.name}: ${mod.effect}` : `[${label}] No Data`;

        return {
            option1: {
                A: getModText(option1Data?.A, 'A'),
                B: getModText(option1Data?.B, 'B'),
                null: "Modifier Inactive"
            },
            option2: {
                A: getModText(option2Data?.A, 'A'),
                B: getModText(option2Data?.B, 'B'),
                null: "Modifier Inactive"
            }
        };
    }, [state.selectedKnight, state.selectedWeapon]);


    const setSelectedKnight = useCallback((k: KnightKey | null) => dispatch({ type: 'SET_KNIGHT', knight: k }), []);
    const setSelectedWeapon = useCallback((w: string | null) => dispatch({ type: 'SET_WEAPON', weapon: w }), []);
    const setSelectedSpell = useCallback((s: string | null) => dispatch({ type: 'SET_SPELL', spell: s }), []);
    
    const toggleBlessing = useCallback((b: string) => dispatch({ type: 'TOGGLE_BLESSING', blessing: b }), []);
    const clearBlessings = useCallback(() => dispatch({ type: 'CLEAR_BLESSINGS' }), []);
    const handleBlessingListItemClick = useCallback((name: string) => {
         if (state.viewedBlessing === name) dispatch({ type: 'SET_VIEWED_BLESSING', blessing: null });
         else dispatch({ type: 'SET_VIEWED_BLESSING', blessing: name });
    }, [state.viewedBlessing]);
    const setViewedBlessing = useCallback((b: string | null) => dispatch({ type: 'SET_VIEWED_BLESSING', blessing: b }), []);

    const setAnvilMode = useCallback((m: 'single' | 'double') => dispatch({ type: 'SET_ANVIL_MODE', mode: m }), []);
    const toggleAnvilUpgrade = useCallback((u: string) => dispatch({ type: 'TOGGLE_ANVIL_UPGRADE', upgrade: u }), []);
    const setSelectedAnvilUpgrades = useCallback((u: string[]) => dispatch({type: 'SET_ANVIL_UPGRADES', upgrades: u}), []);
    
    const setMorganaMode = useCallback((m: 'single' | 'double') => dispatch({ type: 'SET_MORGANA_MODE', mode: m }), []);
    const toggleMorganaModifier = useCallback((m: string) => dispatch({ type: 'TOGGLE_MORGANA_MODIFIER', modifier: m }), []);
    const setSelectedMorganaModifiers = useCallback((m: string[]) => dispatch({type: 'SET_MORGANA_MODIFIERS', modifiers: m}), []);

    const setMerlinMode = useCallback((m: 'single' | 'double') => dispatch({ type: 'SET_MERLIN_MODE', mode: m }), []);
    const toggleMerlinUpgrade = useCallback((u: string) => dispatch({ type: 'TOGGLE_MERLIN_UPGRADE', upgrade: u }), []);
    const setSelectedMerlinUpgrades = useCallback((u: string[]) => dispatch({type: 'SET_MERLIN_UPGRADES', upgrades: u}), []);

    const toggleNimueOption = useCallback((o: 'option1' | 'option2') => dispatch({ type: 'TOGGLE_NIMUE_OPTION', option: o }), []);
    const setNimueSelection = useCallback((s: NimueSelection) => dispatch({ type: 'SET_NIMUE_SELECTION', selection: s }), []);

    const toggleMetastone = useCallback((k: string) => dispatch({ type: 'TOGGLE_SOURCE_OPTION', stoneKey: k }), []);
    const [selectedStone, setSelectedStone] = useState<string | null>(null);
    const setMetastoneOptions = useCallback((o: Record<string, 'A' | 'B'>) => dispatch({ type: 'SET_SOURCE_OPTIONS', options: o }), []);
    
    const setCurrentBuildId = useCallback((id: string | null) => dispatch({ type: 'SET_BUILD_META', id: id || '', name: state.currentBuildName || '' }), [state.currentBuildName]);
    const setCurrentBuildName = useCallback((name: string | null) => dispatch({ type: 'SET_BUILD_META', id: state.currentBuildId || '', name: name || '' }), [state.currentBuildId]);

    const handleNewBuild = useCallback(() => {
        dispatch({ type: 'NEW_BUILD', defaultSourceOptions });
        setSaveMessage('New build started.');
        setTimeout(() => setSaveMessage(null), 3000);
    }, []);

    const handleSaveBuild = useCallback(async (name: string, forceNew: boolean = false) => {
        if (!state.selectedKnight || !state.selectedWeapon || !state.selectedSpell) {
            setSaveMessage('A knight, weapon, and spell must be selected to save.');
            setTimeout(() => setSaveMessage(null), 3000);
            return;
        }

        const buildToSave = constructBuildData(
            name,
            state.selectedKnight,
            state.selectedWeapon,
            state.selectedSpell,
            state.selectedBlessings,
            state.nimueSelection,
            state.anvil,
            state.morgana,
            state.merlin,
            state.source
        );

        let savedData: BuildData | null = null;
        if (state.currentBuildId && !forceNew) {
            savedData = await updateBuild(state.currentBuildId, buildToSave);
            if (savedData) setSaveMessage(`Build "${name}" updated!`);
            else {
                savedData = await saveBuild(buildToSave);
                setSaveMessage(`Build "${name}" saved to your library!`);
            }
        } else {
            savedData = await saveBuild(buildToSave);
            setSaveMessage(`Build "${name}" saved!`);
        }

        if (savedData) {
            dispatch({ type: 'SET_BUILD_META', id: savedData.id, name: savedData.name });
            const allBuilds = await getAllBuilds();
            setSavedBuilds(allBuilds);
        }
        setTimeout(() => setSaveMessage(null), 3000);
    }, [state, saveBuild, updateBuild, getAllBuilds]);

    const handleLoadBuild = useCallback((build: BuildData) => {
        dispatch({ type: 'LOAD_BUILD', build });
    }, []);

    const handleDeleteBuild = useCallback(async (id: string) => {
        const success = await deleteBuild(id);
        if (success) {
            if (id === state.currentBuildId) handleNewBuild();
            const allBuilds = await getAllBuilds();
            setSavedBuilds(allBuilds);
            setSaveMessage('Build deleted.');
            setTimeout(() => setSaveMessage(null), 3000);
        }
    }, [state.currentBuildId, deleteBuild, getAllBuilds, handleNewBuild]);

     const handleShareBuild = useCallback(async () => {
        if (!state.selectedKnight || !state.selectedWeapon || !state.selectedSpell) {
             setSaveMessage('Cannot share an incomplete build.');
             setTimeout(() => setSaveMessage(null), 3000);
             return null;
        }
        const buildToShare: BuildData = {
            ...constructBuildData(
                state.currentBuildName || 'Shared Build',
                state.selectedKnight,
                state.selectedWeapon,
                state.selectedSpell,
                state.selectedBlessings,
                state.nimueSelection,
                state.anvil,
                state.morgana,
                state.merlin,
                state.source
            ),
            id: state.currentBuildId || 'temp-share-id',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        };
        return await shareBuild(buildToShare);
    }, [state, shareBuild]);


    return useMemo(() => ({
        selectedKnight: state.selectedKnight,
        selectedWeapon: state.selectedWeapon,
        selectedSpell: state.selectedSpell,
        selectedBlessings: state.selectedBlessings,
        savedBuilds,
        setSavedBuilds,
        currentBuildId: state.currentBuildId,
        currentBuildName: state.currentBuildName,
        saveMessage,
        setSaveMessage,
        viewedBlessing: state.viewedBlessing,
        
        anvilMode: state.anvil.mode,
        selectedAnvilUpgrades: state.anvil.upgrades,
        nimueSelection: state.nimueSelection,
        currentNimueModifiers,
        morganaMode: state.morgana.mode,
        selectedMorganaModifiers: state.morgana.modifiers,
        merlinMode: state.merlin.mode,
        selectedMerlinUpgrades: state.merlin.upgrades,
        metastoneOptions: state.source,
        metastones: SWORN_DATA.source_modifiers,
        
        selectedCompendiumFae,
        setSelectedCompendiumFae,
        activePanel,
        setActivePanel,
        selectedCompendiumKnight, setSelectedCompendiumKnight,
        closeAllPanels,
        activatePanel,
        
        setSelectedKnight,
        setSelectedWeapon,
        setSelectedSpell,
        toggleBlessing,
        clearBlessings,
        setViewedBlessing,
        
        setCurrentBuildId,
        setCurrentBuildName,
        updateBuild,
        saveBuild,
        handleSaveBuild,
        handleLoadBuild,
        handleDeleteBuild,
        handleNewBuild,
        handleShareBuild,
        handleBlessingListItemClick,
        
        setAnvilMode, toggleAnvilUpgrade, setSelectedAnvilUpgrades,
        toggleNimueOption, setNimueSelection,
        setMorganaMode, toggleMorganaModifier, setSelectedMorganaModifiers,
        setMerlinMode, toggleMerlinUpgrade, setSelectedMerlinUpgrades,
        toggleMetastone, setSelectedStone, selectedStone, setMetastoneOptions,
        
        knightKeys,
        containerRef,
        contextMenu,
        openContextMenu,
        closeContextMenu,
        defaultSourceOptions
    }), [
        state,
        savedBuilds,
        saveMessage,
        currentNimueModifiers,
        selectedCompendiumFae,
        setSelectedCompendiumFae,
        activePanel,
        setActivePanel,
        selectedCompendiumKnight, 
        setSelectedCompendiumKnight,
        closeAllPanels,
        activatePanel,
        setSelectedKnight,
        setSelectedWeapon,
        setSelectedSpell,
        toggleBlessing,
        clearBlessings,
        setViewedBlessing,
        setCurrentBuildId,
        setCurrentBuildName,
        updateBuild,
        saveBuild,
        handleSaveBuild,
        handleLoadBuild,
        handleDeleteBuild,
        handleNewBuild,
        handleShareBuild,
        handleBlessingListItemClick,
        setAnvilMode, toggleAnvilUpgrade, setSelectedAnvilUpgrades,
        toggleNimueOption, setNimueSelection,
        setMorganaMode, toggleMorganaModifier, setSelectedMorganaModifiers,
        setMerlinMode, toggleMerlinUpgrade, setSelectedMerlinUpgrades,
        toggleMetastone, setSelectedStone, selectedStone, setMetastoneOptions,
        knightKeys,
        contextMenu,
        openContextMenu,
        closeContextMenu
    ]);
};
